import React, { useMemo, useState, useEffect } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';

import { KOLColorAndEmojiPresenter, ColorPair } from '../../app/kols/presenters/KOLColorAndEmojiPresenter';
import { KOLEbookIdsPresenter } from '../../app/kols/presenters/KOLEbookIdsPresenter';
import { KOLTitleDescriptionPresenter } from '../../app/kols/presenters/KOLTitleDescriptionPresenter';
import { KOLUserSearchPresenter } from '../../app/kols/presenters/KOLUserSearchPresenter';
import Breadcrumbs from '../../components/Breadcrumbs';
import LoadingModal from '../../components/modal/LoadingModal';
import ToastComponent, { IToastType } from '../../components/Toast/ToastComponent';
import useMutationCreateEbookCollection from '../../hooks/useMutationCreateEbookCollection';
import useMutationDeletePintoEbookCollection from '../../hooks/useMutationDeletePintoEbookCollection';
import useMutationEditEbookCollection from '../../hooks/useMutationEditEbookCollection';
import useQueryPintoEbookCollection from '../../hooks/useQueryPintoEbookCollection';
import useQueryPintoEbookCollectionItems from '../../hooks/useQueryPintoEbookCollectionItems';

function EditKOLPage() {
  // query and resume state
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const kolId = searchParams.get('id');

  //  covers

  const [titleDescription, setTitleDescription] = useState({ title: '', description: '' });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedUser, setSelectedUser] = useState<any | undefined>(undefined);
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(undefined);
  const [ebookIds, setEbookIds] = useState<{ ebookIds: string[] }>({ ebookIds: [] });
  const [colorAndEmoji, setColorAndEmoji] = useState<{ color?: ColorPair; emoji?: string }>({});

  const history = useHistory();
  const { createEbookCollection } = useMutationCreateEbookCollection();
  const { editEbookCollection } = useMutationEditEbookCollection();
  const { deletePintoEbookCollection } = useMutationDeletePintoEbookCollection();

  // Fetch KOL data for edit
  const { fetchPintoEbookCollection, pintoEbookCollection, isLoadingPintoEbookCollection } = useQueryPintoEbookCollection();
  // Fetch KOL items for ebookIds
  const { fetchPintoEbookCollectionItems, pintoEbookCollectionItems, isLoadingPintoEbookCollectionItems } = useQueryPintoEbookCollectionItems();

  useEffect(() => {
    if (kolId) {
      fetchPintoEbookCollection(kolId);
      fetchPintoEbookCollectionItems(kolId);
    }
  }, [kolId, fetchPintoEbookCollection, fetchPintoEbookCollectionItems]);

  // Prefill form state when KOL data is loaded
  useEffect(() => {
    if (pintoEbookCollection) {
      setTitleDescription({
        title: pintoEbookCollection.title || '',
        description: pintoEbookCollection.description || '',
      });
      const user = {
        _id: pintoEbookCollection.user?._id,
        displayName: pintoEbookCollection.user?.displayName,
        username: pintoEbookCollection.user?.username,
        email: pintoEbookCollection.user?.email,
        profileImage: pintoEbookCollection.user?.profileImage,
        type: pintoEbookCollection.user?.type,
        isPremium: pintoEbookCollection.user?.isPremium,
      };
      setSelectedUser(user);
      setSelectedUserId(pintoEbookCollection.user?._id || undefined);
      setColorAndEmoji({
        color:
          pintoEbookCollection.hexColorCode && pintoEbookCollection.darkModeHexColorCode
            ? { light: pintoEbookCollection.hexColorCode, dark: pintoEbookCollection.darkModeHexColorCode }
            : undefined,
        emoji: pintoEbookCollection.emoji,
      });
    }
  }, [pintoEbookCollection]);

  // Prefill ebookIds when items are loaded
  useEffect(() => {
    if (pintoEbookCollectionItems) {
      setEbookIds({
        ebookIds: pintoEbookCollectionItems.edges.map((edge) => edge.node._id),
      });
    }
  }, [pintoEbookCollectionItems]);

  const onSubmitPublish = async () => {
    if (!selectedUserId) {
      ToastComponent({ label: 'Please select a user.', type: IToastType.ERROR });
      return;
    }
    if (!colorAndEmoji.color) {
      ToastComponent({ label: 'Please select a color palette.', type: IToastType.ERROR });
      return;
    }
    if (!colorAndEmoji.emoji) {
      ToastComponent({ label: 'Please select an emoji.', type: IToastType.ERROR });
      return;
    }
    if (!titleDescription.title.trim()) {
      ToastComponent({ label: 'Please enter a title.', type: IToastType.ERROR });
      return;
    }
    if (!titleDescription.description.trim()) {
      ToastComponent({ label: 'Please enter a description.', type: IToastType.ERROR });
      return;
    }
    if (!ebookIds.ebookIds.length) {
      ToastComponent({ label: 'Please enter at least one ebook ID.', type: IToastType.ERROR });
      return;
    }
    const input = {
      title: titleDescription.title,
      description: titleDescription.description,
      userId: selectedUserId,
      emoji: colorAndEmoji.emoji,
      hexColorCode: colorAndEmoji.color.light,
      darkModeHexColorCode: colorAndEmoji.color.dark,
      upsertedItems: ebookIds.ebookIds.map((ebookId, idx) => ({ ebookId, order: idx })),
    };
    try {
      if (kolId) {
        // Edit
        const res = await editEbookCollection(kolId, input);
        if (res.success && res.data?._id) {
          ToastComponent({ label: 'Edit successful!', type: IToastType.SUCCESS });
          history.push(`/kols/detail?id=${res.data._id}`);
        }
      } else {
        // Create
        const res = await createEbookCollection(input);
        if (res.success && res.data?._id) {
          ToastComponent({ label: 'Create successful!', type: IToastType.SUCCESS });
          history.push(`/kols/detail?id=${res.data._id}`);
        }
      }
    } catch (err) {
      ToastComponent({ label: 'An error occurred.', type: IToastType.ERROR });
    }
  };

  const onDelete = async () => {
    if (!kolId) return;
    if (!window.confirm('Are you sure you want to delete this KOL?')) return;
    try {
      const res = await deletePintoEbookCollection(kolId);
      if (res.success) {
        ToastComponent({ label: 'Delete successful!', type: IToastType.SUCCESS });
        history.push('/kols');
      }
    } catch (err) {
      ToastComponent({ label: 'An error occurred.', type: IToastType.ERROR });
    }
  };

  const handleUserSelection = (userId: string) => {
    setSelectedUserId(userId);
    // For display purposes, we can keep the selectedUser object
    // but the important part is the selectedUserId
  };

  if (isLoadingPintoEbookCollection || isLoadingPintoEbookCollectionItems) {
    return <LoadingModal />;
  }

  return (
    <div className="p-8 space-y-8">
      <div className="space-y-12">
        <div className="sticky top-0 z-50 px-5 bg-componentsBgGrouped02">
          {/* section: breadcrumbs */}
          <Breadcrumbs items={[{ name: 'KOLs', url: '/kols' }, { name: kolId ? 'Edit KOL' : 'Create KOL' }]} />
          {/* section: title */}
          <div className="flex justify-between pt-5">
            <div className="flex space-x-8 items-baseline">
              {!kolId && <h1 className="font-dbh text-[28px]">Create KOL</h1>}
              {pintoEbookCollection?.title ? <h1 className="text-xl">{pintoEbookCollection.title}</h1> : null}
            </div>
            <div className="flex items-center space-x-12">
              <Link to="/kols">
                <button className="btn btn-outline">CANCEL</button>
              </Link>
              {kolId && (
                <button className="btn btn-outline btn-error" onClick={onDelete}>
                  DELETE
                </button>
              )}
              <button className="space-x-4 btn btn-error" disabled={!selectedUserId} onClick={onSubmitPublish}>
                <div>{kolId ? 'SAVE' : 'PUBLISH'}</div>
              </button>
            </div>
          </div>

          {/* section: main */}
          <div className="mb-0 divider opacity-10"></div>
        </div>
      </div>
      <KOLUserSearchPresenter selectedUser={selectedUser} onSelectedUser={handleUserSelection} isEditUser={!!kolId} />
      <KOLTitleDescriptionPresenter value={titleDescription} onChange={setTitleDescription} />
      <KOLEbookIdsPresenter value={ebookIds} onChange={setEbookIds} />
      <KOLColorAndEmojiPresenter value={colorAndEmoji} onChange={setColorAndEmoji} />
    </div>
  );
}

export default EditKOLPage;
